import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import logCustomRoutePOIExploreSelected from '@alltrails/amplitude/events/logCustomRoutePOIExploreSelected';
import { useClientNavigationPushState } from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useValueRef from '@alltrails/core/hooks/useValueRef';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Edit from '@alltrails/denali/icons/Edit';
import Gallery from '@alltrails/denali/icons/Gallery';
import { updateLocationResult } from '@alltrails/explore-service/redux/exploreServiceSlice';
import useCustomRouteBuilderPath from '@alltrails/layout/hooks/useCustomRouteBuilderPath';
import useCustomRouteBuilderPushState from '@alltrails/layout/hooks/useCustomRouteBuilderPushState';
import useTapAnywhereMenuAnalytics from '../../../hooks/useTapAnywhereMenuAnalytics';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { setPendingFlyTo, updateActivePOI, updateSelectedPOI, updateShowPOIPopupMenu } from '../../../redux/reducer';
import useRouteToPoint from '../../CustomRoute/DrawControls/useRouteToPoint';
import ActivePOIPopupOptions from './ActivePOIPopupOptions';

type ActivePOIRoutingOptionsProps = {
  canAddToRoute?: boolean;
};

const ActivePOIRoutingOptions = ({ canAddToRoute = false }: ActivePOIRoutingOptionsProps) => {
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const languageRegionCode = useLanguageRegionCode();
  const pushState = useClientNavigationPushState();
  const builderPath = useCustomRouteBuilderPath();
  const builderPushState = useCustomRouteBuilderPushState();
  const { activePOI, isSmartRouting, showPOIPopupMenu } = useAppSelector(state => ({
    activePOI: state.map.activePOI,
    isSmartRouting: state.customRoute.isSmartRouting,
    showPOIPopupMenu: state.map.showPOIPopupMenu
  }));
  const isSmartRoutingRef = useValueRef(isSmartRouting);
  const { routeToPoint } = useRouteToPoint(isSmartRoutingRef);
  const { logMenuAction } = useTapAnywhereMenuAnalytics({
    isOpen: Boolean(activePOI && showPOIPopupMenu),
    isAllTrailsFeature: true,
    lat: activePOI?._geoloc?.lat,
    lng: activePOI?._geoloc?.lng,
    pointType: 'poi',
    source: 'tap',
    sourceId: activePOI ? String(activePOI.ID) : undefined
  });

  const returnTo = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    return window.location.pathname + window.location.search;
  }, []);

  const buildCustomRouteUrl = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    if (activePOI?._geoloc) {
      params.set('lat', activePOI?._geoloc?.lat.toString());
      params.set('lng', activePOI?._geoloc?.lng.toString());
    }
    if (returnTo) {
      params.set('returnTo', returnTo);
    }
    return wrapUrlSafe(`${builderPath}/new?${params.toString()}`, languageRegionCode);
  }, [builderPath, searchParams, returnTo, languageRegionCode, activePOI?._geoloc]);

  const exploreUrl = useMemo(
    () => wrapUrlSafe(`/explore/${activePOI?.slug}?${searchParams.toString()}`, languageRegionCode),
    [activePOI, languageRegionCode, searchParams]
  );

  const popupOptions = useMemo(
    () => [
      {
        leadingIcon: { icon: { Component: Gallery, size: 'xs' } },
        onClick: () => {
          logMenuAction('explore this spot');
          if (canAddToRoute) {
            dispatch(updateSelectedPOI(activePOI));
            dispatch(updateLocationResult(activePOI));
            dispatch(updateShowPOIPopupMenu(false));
            dispatch(updateActivePOI(undefined));
            if (activePOI?.ID) {
              logCustomRoutePOIExploreSelected({ poi_id: activePOI?.ID });
            }
          } else {
            // Close the popup before navigating so the remounted map components don't see it as still open
            dispatch(updateShowPOIPopupMenu(false));
            dispatch(updateActivePOI(undefined));
            pushState(exploreUrl);
          }
        },
        title: <FormattedMessage defaultMessage="Explore this spot" />
      },
      {
        leadingIcon: { icon: { Component: Edit, size: 'xs' } },
        onClick: () => {
          logMenuAction('build custom route');
          if (activePOI?._geoloc) {
            if (canAddToRoute) {
              routeToPoint({ coordinates: { lat: activePOI._geoloc.lat, lng: activePOI._geoloc.lng }, isPOIAdd: true });
              dispatch(updateActivePOI(undefined));
              dispatch(updateSelectedPOI(undefined));
              dispatch(updateShowPOIPopupMenu(false));
              dispatch(updateLocationResult(undefined));
            } else {
              dispatch(updateShowPOIPopupMenu(false));
              dispatch(updateActivePOI(undefined));
              dispatch(setPendingFlyTo({ center: [activePOI._geoloc.lng, activePOI._geoloc.lat], zoom: 17 }));
              builderPushState(buildCustomRouteUrl());
            }
          }
        },
        title: canAddToRoute ? <FormattedMessage defaultMessage="Add to route" /> : <FormattedMessage defaultMessage="Build custom route" />
      }
    ],
    [builderPushState, activePOI, canAddToRoute, dispatch, exploreUrl, pushState, buildCustomRouteUrl, routeToPoint, logMenuAction]
  );

  return (
    <>
      {activePOI && showPOIPopupMenu && (
        <ActivePOIPopupOptions options={popupOptions} poi={activePOI} onCopyCoordinates={() => logMenuAction('copy coordinates')} />
      )}
    </>
  );
};

export default ActivePOIRoutingOptions;
